import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Star, Newspaper, Edit, Eye, Calendar, User, 
  ArrowUp, ArrowDown, AlertCircle, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

const PRIORITY_CONFIG = {
  1: { label: "Breaking News", color: "bg-red-500 text-white", description: "Highest visibility, appears first" },
  2: { label: "Top Story", color: "bg-orange-500 text-black", description: "Featured prominently" },
  3: { label: "Featured", color: "bg-yellow-500 text-black", description: "Highlighted content" }
};

export const TopStoriesTab = () => {
  const [stories, setStories] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [topRes, allRes] = await Promise.all([
        axios.get(`${API}/news/admin/top-stories`, { headers: getAuthHeader() }),
        axios.get(`${API}/news/admin/articles`, { headers: getAuthHeader() })
      ]);
      setStories(topRes.data);
      setAllArticles(allRes.data.filter(a => a.status === 'published' && (!a.priority || a.priority > 3)));
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePriority = async (articleId, newPriority) => {
    setUpdating(articleId);
    try {
      await axios.put(
        `${API}/news/admin/articles/${articleId}`,
        { priority: newPriority },
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (err) {
      console.error("Error updating priority:", err);
    } finally {
      setUpdating(null);
    }
  };

  const handleRemoveFromTopStories = async (articleId) => {
    await handleUpdatePriority(articleId, 5);
  };

  const handleAddToTopStories = async (articleId, priority) => {
    await handleUpdatePriority(articleId, priority);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  // Group stories by priority
  const storiesByPriority = {
    1: stories.filter(s => s.priority === 1),
    2: stories.filter(s => s.priority === 2),
    3: stories.filter(s => s.priority === 3)
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white flex items-center gap-3">
            <Star className="w-6 h-6 text-orange-500" />
            Top Stories
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Manage which articles appear as top stories on the News page
          </p>
        </div>
        <Button onClick={fetchData} className="bg-zinc-800 text-white hover:bg-zinc-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Priority Sections */}
      {[1, 2, 3].map((priority) => (
        <div key={priority} className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 text-xs font-bold ${PRIORITY_CONFIG[priority].color}`}>
              Priority {priority}
            </span>
            <span className="text-white font-bold">{PRIORITY_CONFIG[priority].label}</span>
            <span className="text-zinc-500 text-sm">- {PRIORITY_CONFIG[priority].description}</span>
          </div>

          {storiesByPriority[priority].length === 0 ? (
            <div className="bg-zinc-900/30 border border-dashed border-white/10 p-8 text-center">
              <p className="text-zinc-500">No articles with priority {priority}</p>
              <p className="text-zinc-600 text-xs mt-1">Drag articles here or set their priority in the News tab</p>
            </div>
          ) : (
            <div className="space-y-2">
              {storiesByPriority[priority].map((story) => (
                <div
                  key={story.id}
                  className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-white/10 hover:bg-zinc-900/70"
                >
                  {/* Thumbnail */}
                  {story.featured_image ? (
                    <img src={story.featured_image} alt="" className="w-16 h-12 object-cover bg-zinc-800" />
                  ) : (
                    <div className="w-16 h-12 bg-zinc-800 flex items-center justify-center">
                      <Newspaper className="w-4 h-4 text-zinc-600" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium line-clamp-1">{story.title}</h4>
                    <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {story.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(story.published_at)}
                      </span>
                    </div>
                  </div>

                  {/* Priority Controls */}
                  <div className="flex items-center gap-1">
                    {priority > 1 && (
                      <button
                        onClick={() => handleUpdatePriority(story.id, priority - 1)}
                        disabled={updating === story.id}
                        className="p-2 text-zinc-500 hover:text-green-500 disabled:opacity-50"
                        title="Increase priority"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    )}
                    {priority < 3 && (
                      <button
                        onClick={() => handleUpdatePriority(story.id, priority + 1)}
                        disabled={updating === story.id}
                        className="p-2 text-zinc-500 hover:text-yellow-500 disabled:opacity-50"
                        title="Decrease priority"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveFromTopStories(story.id)}
                      disabled={updating === story.id}
                      className="p-2 text-zinc-500 hover:text-red-500 disabled:opacity-50"
                      title="Remove from top stories"
                    >
                      <span className="text-xs">Remove</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Available Articles */}
      <div className="mt-12 pt-8 border-t border-white/10">
        <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">
          Add to Top Stories
        </h3>
        <p className="text-zinc-500 text-sm mb-4">
          Select published articles to feature as top stories
        </p>

        {allArticles.length === 0 ? (
          <div className="bg-zinc-900/30 border border-white/10 p-8 text-center">
            <AlertCircle className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500">All published articles are already in Top Stories or there are no published articles</p>
          </div>
        ) : (
          <div className="grid gap-2 max-h-[400px] overflow-y-auto">
            {allArticles.slice(0, 20).map((article) => (
              <div
                key={article.id}
                className="flex items-center gap-4 p-3 bg-zinc-900/30 border border-white/5 hover:bg-zinc-900/50"
              >
                {/* Thumbnail */}
                {article.featured_image ? (
                  <img src={article.featured_image} alt="" className="w-12 h-9 object-cover bg-zinc-800" />
                ) : (
                  <div className="w-12 h-9 bg-zinc-800 flex items-center justify-center">
                    <Newspaper className="w-3 h-3 text-zinc-600" />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-white text-sm font-medium line-clamp-1">{article.title}</h4>
                  <p className="text-xs text-zinc-500">{formatDate(article.published_at)}</p>
                </div>

                {/* Add Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleAddToTopStories(article.id, 1)}
                    disabled={updating === article.id}
                    className="px-2 py-1 text-[10px] font-bold bg-red-500/20 text-red-500 hover:bg-red-500/30 disabled:opacity-50"
                  >
                    Breaking
                  </button>
                  <button
                    onClick={() => handleAddToTopStories(article.id, 2)}
                    disabled={updating === article.id}
                    className="px-2 py-1 text-[10px] font-bold bg-orange-500/20 text-orange-500 hover:bg-orange-500/30 disabled:opacity-50"
                  >
                    Top Story
                  </button>
                  <button
                    onClick={() => handleAddToTopStories(article.id, 3)}
                    disabled={updating === article.id}
                    className="px-2 py-1 text-[10px] font-bold bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 disabled:opacity-50"
                  >
                    Featured
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TopStoriesTab;
